import {Component, EventEmitter, OnInit, Output} from '@angular/core';
import {AbstractControl, FormArray, FormControl, FormGroup, Validators} from '@angular/forms';
import {Router} from '@angular/router';
import * as moment from 'moment';
import {LangChangeEvent, TranslateService} from '@ngx-translate/core';
import * as FileSaver from 'file-saver';
import {PensionCalculatorSetupService} from '../pension-calculator-setup/pension-calculator-setup.service';
import {PensionCalculatorService} from './pension-calculator.service';
import {DocumentResourceService} from '../../../shared/services/document-resource.service';
import {HomeService} from '../../home/services/home.service'
import {AzidValidators} from '../../../shared/validators/azid-validators';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import {PrincipalService} from '../../../shared';
import {
  CalculatedPensionEmployeeResponse,
  CalculatedPensionResponse,
  InvestmentAllocation,
  PensionCalculatorRequest,
  ScenarioOption
} from '../../../shared/types/pension-calculator.type';
import {DocumentDownloadRequest, DocumentUploadRequest} from '../../../shared/types/document.type';
import {
  CURRENCY_CONSTANTS,
  FILE_TYPE_CONSTANTS,
  GENERAL_CONSTANTS,
  INVESTMENT_TYPES,
  ROLE_CONSTANTS
} from '../../../shared/constants/constant';
import {numberWithCommas} from '../../../shared/utils/string-utils';
import {IMyDpOptions} from 'mydatepicker';
import {TokenService} from '../../../shared/services/token.service';

@Component({
  selector: 'app-pension-calculator',
  templateUrl: './pension-calculator.component.html',
  styleUrls: ['./pension-calculator.component.css']
})
export class PensionCalculatorComponent implements OnInit {
  @Output() notify = new EventEmitter();
  public currencySymbol: string = CURRENCY_CONSTANTS.RP;
  public error: boolean = false;
  public showForm: boolean = false;
  public ageFailure: boolean = true;
  public validationOffOnload: boolean = false;
  public ageError: boolean = false;
  public errorMessage: string = GENERAL_CONSTANTS.EMPTY_STRING;
  public defaultsetupValues: Array<Object>;
  public birthDate: Object;
  public userRole: string = GENERAL_CONSTANTS.EMPTY_STRING;
  public isEmployee: boolean;
  public browserLang: string;
  public lang: string;
  public guide = false;
  public showPensionResult: boolean = false;
  public showDetailPensionResult: boolean = false;
  public showDetailLink: boolean = false;
  public showLink: boolean = false;
  public hideLink: boolean = false;
  public uploadedFile = false;
  public noFile = false;
  public noTitle = false;
  public furtherUpload = false;
  public displayMessage: string = GENERAL_CONSTANTS.EMPTY_STRING;
  public riskProfile: string = GENERAL_CONSTANTS.EMPTY_STRING;

  public investmentAllocation: InvestmentAllocation[] = INVESTMENT_TYPES;
  public predefinedScenarios: ScenarioOption[] = [
    { label: 'High', value: 3 },
    { label: 'Moderate', value: 2 },
    { label: 'Low', value: 1 }
  ];
  public predefinedPercentages: number[] = [];
  public retirementAgeRange: number[] = [];

  public attachFile: File;
  public success: Boolean = false;
  public docStatus;
  public token: string;
  public balanceAmount: string;
  public accountDetails: any;

  public calculatedPension: CalculatedPensionResponse;
  public calculatedPensionEmployee: CalculatedPensionEmployeeResponse;
  public documentDownloadRequest: DocumentDownloadRequest;
  public pensionCalculatorForm: FormGroup;
  public pensionCalculatorEmployerForm: FormGroup;

  public dateOfBirth: AbstractControl;
  public userGender: AbstractControl;
  public monthlyIncome: AbstractControl;
  public retirementAge: AbstractControl;
  public pensionSavingBalance: AbstractControl;
  public annualDevResults: AbstractControl;
  public totalCurrentMonthlyContribution: AbstractControl;
  public avgSalaryIncYear: AbstractControl;
  public effectiveDate: AbstractControl;
  public myDatePickerOptions: IMyDpOptions;

  public numberMask = createNumberMask({
    prefix: GENERAL_CONSTANTS.EMPTY_STRING,
    suffix: GENERAL_CONSTANTS.PERCENTAGE,
    integerLimit: '3',
    allowDecimal: true,
    decimalLimit: 10,
    includeThousandsSeparator: false
    // This will put the dollar sign at the end, with a space.
  });
  public yearMask = createNumberMask({
    prefix: GENERAL_CONSTANTS.EMPTY_STRING,
    suffix: ' Tahun',
    integerLimit: '3',
    includeThousandsSeparator: false,
    thousandsSeparatorSymbol: GENERAL_CONSTANTS.EMPTY_STRING
    // This will put the dollar sign at the end, with a space.
  });
  public currMask = createNumberMask({
    prefix: this.currencySymbol + ' ',
    suffix: GENERAL_CONSTANTS.EMPTY_STRING,
    thousandsSeparatorSymbol: GENERAL_CONSTANTS.COMMA,
    allowDecimal: true,
    decimalSymbol: GENERAL_CONSTANTS.DOT,
    decimalLimit: '48'
    // This will put the dollar sign at the end, with a space.
  });

  constructor(
    public pensionSetupService: PensionCalculatorSetupService,
    public pensionCalculatorService: PensionCalculatorService,
    public homeService: HomeService,
    private tokenService: TokenService,
    private translate: TranslateService,
    public principalService: PrincipalService,
    private documentResourceService: DocumentResourceService,
    public router: Router
  ) {
    this.lang = translate.currentLang;

    this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
      this.browserLang = event.lang;
      this.lang = this.browserLang;
    });
    this.isEmployee = false;
    this.myDatePickerOptions = {
      dateFormat: "dd-mm-yyyy",
      showTodayBtn: true,
      minYear: 1990,
      yearSelector: true,
      markCurrentDay: true,
      showSelectorArrow: true,
      showClearDateBtn: false,
      editableDateField: true,
      disableSince: {
        year: moment().year(), // this year
        month: moment().get("month") + 1, // this month
        day: moment().date() + 1, // tommorrow
      },
    };

    this.token = this.tokenService.secureGet("token");
    this.accountDetails = this.tokenService.secureGet("accountDetails");

    this.initializeForms();
    this.initializeControls();
  }

  ngOnInit() {
    this.fetchUserRole();
    this.setDefaultValue();
    this.formValueGetChanged();
    this.generateDefaultRange();
  }

  public initializeForms() {
    let formattedDob = GENERAL_CONSTANTS.EMPTY_STRING;
    if (this.accountDetails && this.accountDetails.dob) {
        formattedDob = moment(this.accountDetails.dob).format('DD-MM-YYYY');
        this.balanceAmount = this.accountDetails.balanceAmount ?? 0;
    }

    this.pensionCalculatorForm = new FormGroup({
      dateOfBirth: new FormControl(formattedDob, Validators.required),
      userGender: new FormControl(GENERAL_CONSTANTS.EMPTY_STRING, Validators.required),
      monthlyIncome: new FormControl(GENERAL_CONSTANTS.EMPTY_STRING, Validators.compose([Validators.required, AzidValidators.isSetupMoneyFieldValidWithFormControl])),
      retirementAge: new FormControl(GENERAL_CONSTANTS.EMPTY_STRING, Validators.required),
      pensionSavingBalance: new FormControl(this.balanceAmount, Validators.required),
      annualDevResults: new FormControl(GENERAL_CONSTANTS.EMPTY_STRING, Validators.compose([Validators.required, AzidValidators.isSetupCalculatorValidWithFormControl])),
      totalCurrentMonthlyContribution: new FormControl(GENERAL_CONSTANTS.EMPTY_STRING, Validators.compose([Validators.required, AzidValidators.isSetupMoneyFieldValidWithFormControl])),
      avgSalaryIncYear: new FormControl(GENERAL_CONSTANTS.EMPTY_STRING, Validators.compose([Validators.required, AzidValidators.isSetupCalculatorValidWithFormControl])),
      investments: new FormArray(
        this.investmentAllocation
          .map(investment => this.createInvestmentGroup(investment))
      )
    });

    this.pensionCalculatorEmployerForm = new FormGroup({
      effectiveDate: new FormControl('', Validators.required),
      avgSalaryIncYear: new FormControl(GENERAL_CONSTANTS.EMPTY_STRING, Validators.compose([Validators.required, AzidValidators.isSetupCalculatorValidWithFormControl])),
      investments: new FormArray(
        this.investmentAllocation
          .filter(investment => investment.investmentType !== 4 && investment.investmentType !== 5)
          .map(investment => this.createInvestmentGroup(investment))
      )
    });
  }

  public initializeControls() {
    this.dateOfBirth = this.pensionCalculatorForm.get("dateOfBirth");
    this.userGender = this.pensionCalculatorForm.get("userGender");
    this.monthlyIncome = this.pensionCalculatorForm.get("monthlyIncome");
    this.retirementAge = this.pensionCalculatorForm.get("retirementAge");
    this.pensionSavingBalance =  this.pensionCalculatorForm.get("pensionSavingBalance");
    this.annualDevResults = this.pensionCalculatorForm.get("annualDevResults");
    this.totalCurrentMonthlyContribution = this.pensionCalculatorForm.get("totalCurrentMonthlyContribution");
    this.avgSalaryIncYear = this.pensionCalculatorForm.get("avgSalaryIncYear");

    this.effectiveDate = this.pensionCalculatorEmployerForm.get("effectiveDate");
    this.avgSalaryIncYear = this.pensionCalculatorEmployerForm.get("avgSalaryIncYear");
  }

  get investments(): FormArray {
    return this.pensionCalculatorForm.get('investments') as FormArray;
  }

  get investmentsEmployer(): FormArray {
    return this.pensionCalculatorEmployerForm.get('investments') as FormArray;
  }

  public fetchUserRole() {
    this.principalService.identity(true).then((account) => {
      this.userRole = account.authorities[0];
      if (this.userRole === ROLE_CONSTANTS.ROLE_EMPLOYEE) {
        this.isEmployee = true;
      }

      if (account == null) {
        this.router.navigate(['login']);
        this.error = true;
      }
    },(error) => {
      this.router.navigate(['login']);
      this.error = true;
    });
  }

  public createInvestmentGroup(investment: any): FormGroup {
    return new FormGroup({
      investmentType: new FormControl(false),
      investmentName: new FormControl(investment.investmentName, Validators.required),
      investmentPercentage: new FormControl({ value: GENERAL_CONSTANTS.EMPTY_STRING, disabled: true },[Validators.required]),
      investmentScenario: new FormControl({ value: GENERAL_CONSTANTS.EMPTY_STRING, disabled: true },[Validators.required])
    });
  }

  public generateDefaultRange(): void {
    for (let age = 55; age <= 65; age++) {
      this.retirementAgeRange.push(age);
    }

    for (let percent = 0; percent <= 100; percent += 10) {
      this.predefinedPercentages.push(percent);
    }
  }

  public calculatePension(formArray: FormArray, form: FormGroup): void {
    this.showDetailPensionResult = false;

    const totalPercentage = this.percentageInputValidation(formArray);
    if (totalPercentage !== 100) {
      this.showError('The total investment percentage must equal 100%.');
      return;
    }

    if (!this.uploadedFile && !this.isEmployee) {
      this.showError('The participant data file need to be upload');
      return;
    }

    const payload: PensionCalculatorRequest = this.constructPayload(formArray, form, this.isEmployee);
    this.initiatePensionCalculation(this.isEmployee, this.token, payload);
  }

  private initiatePensionCalculation(isEmployee: boolean, token: string, payload: PensionCalculatorRequest): void {
    const calculateMethod = isEmployee
        ? this.pensionCalculatorService.calculatePensionEmployee.bind(this.pensionCalculatorService)
        : this.pensionCalculatorService.calculatePension.bind(this.pensionCalculatorService);

    calculateMethod(token, payload, this.attachFile).subscribe({
      next: data => {
        if (data.responseCode == 400) {
            this.showError(data.responseDescription);
            this.showPensionResult = false;
            this.showDetailPensionResult = false;

        } else {
            this.pensionCalculateResponse(data);
            this.triggerScrollToOffsetOnly(2000);
        }

      },
      error: error => {
          console.log("error", error);
          this.handleError();
      }
    });
  }

  private handleError(): void {
    this.showError("Server Error");
    this.showPensionResult = false;
    this.showDetailPensionResult = false;
  }

  public pensionCalculateResponse(data): void {
    this.error = false;
    this.showPensionResult = true;
    this.showDetailLink = true;
    this.showDetailPensionResult = true;

    if (data.replyMessageList && data.replyMessageList.length > 0) {
      const response = data.replyMessageList[0];
      if (this.isEmployee) {
        const scenarioLow = response.scenarios.low;
        const scenarioMid = response.scenarios.middle;
        const scenarioHigh = response.scenarios.high;

        this.calculatedPensionEmployee = {
          initialBalance: numberWithCommas(response.initialBalance ?? 0),
          totalContributions: numberWithCommas(response.totalContributions ?? 0),
          pensionAge: response.pensionAge,
          taxes: [0, 5],
          scenarios: {
            low: {
              managementFees: numberWithCommas(scenarioLow.managementFees ?? 0),
              projectedInvestmentReturn: numberWithCommas(scenarioLow.projectedInvestmentReturn ?? 0),
              balanceBeforeTax: numberWithCommas(scenarioLow.balanceBeforeTax ?? 0),
              tax: {
                amountTaxedAtZeroPercent: numberWithCommas(scenarioLow.tax.amountTaxedAtZeroPercent ?? 0),
                amountTaxedAtFivePercent: numberWithCommas(scenarioLow.tax.amountTaxedAtFivePercent ?? 0),
              },
              balanceAfterTax: numberWithCommas(scenarioLow.balanceAfterTax ?? 0),
              replacementRatioPercent: scenarioLow.replacementRatioPercent
            },
            middle: {
              managementFees: numberWithCommas(scenarioMid.managementFees ?? 0),
              projectedInvestmentReturn: numberWithCommas(scenarioMid.projectedInvestmentReturn ?? 0),
              balanceBeforeTax: numberWithCommas(scenarioMid.balanceBeforeTax ?? 0),
              tax: {
                amountTaxedAtZeroPercent: numberWithCommas(scenarioMid.tax.amountTaxedAtZeroPercent ?? 0),
                amountTaxedAtFivePercent: numberWithCommas(scenarioMid.tax.amountTaxedAtFivePercent ?? 0),
              },
              balanceAfterTax: numberWithCommas(scenarioMid.balanceAfterTax ?? 0),
              replacementRatioPercent: scenarioMid.replacementRatioPercent
            },
            high: {
              managementFees: numberWithCommas(scenarioHigh.managementFees ?? 0),
              projectedInvestmentReturn: numberWithCommas(scenarioHigh.projectedInvestmentReturn ?? 0),
              balanceBeforeTax: numberWithCommas(scenarioHigh.balanceBeforeTax ?? 0),
              tax: {
                amountTaxedAtZeroPercent: numberWithCommas(scenarioHigh.tax.amountTaxedAtZeroPercent ?? 0),
                amountTaxedAtFivePercent: numberWithCommas(scenarioHigh.tax.amountTaxedAtFivePercent ?? 0),
              },
              balanceAfterTax: numberWithCommas(scenarioHigh.balanceAfterTax ?? 0),
              replacementRatioPercent: scenarioHigh.replacementRatioPercent
            }
          }
        };
      } else {
        this.calculatedPension = {
          presentValueBenefitObligation: numberWithCommas(response.presentValueBenefitObligation ?? 0),
          currentServiceCost: numberWithCommas(response.currentServiceCost ?? 0),
          averageFutureService: numberWithCommas(response.averageFutureService ?? 0),
          totalMonthlySalary: numberWithCommas(response.totalMonthlySalary ?? 0),
          totalYearlySalary: numberWithCommas(response.totalYearlySalary ?? 0),
          totalBenefitAtRetirement: numberWithCommas(response.totalBenefitAtRetirement ?? 0),
          totalEmployees: response.totalEmployees,
        }
      }
    }
  }

  public toggleInvestment(formArray: FormArray, index: number): void {
    const investmentGroup = formArray.at(index) as FormGroup;
    const isSelected = investmentGroup.get('investmentType').value;

    if (isSelected) {
      investmentGroup.get('investmentPercentage').enable();
      investmentGroup.get('investmentScenario').enable();

      const activeInvestment = formArray.controls.find((c, i) =>
        c.get('investmentType').value && i !== index
      );

      if (activeInvestment) {
        const currentScenario = activeInvestment.get('investmentScenario').value;
        investmentGroup.get('investmentScenario').setValue(currentScenario);
      }

      this.subscribeToScenarioChanges(formArray, index);
    } else {
      investmentGroup.get('investmentPercentage').disable();
      investmentGroup.get('investmentScenario').disable();
      investmentGroup.get('investmentPercentage').setValue(0);
      investmentGroup.get('investmentScenario').setValue(3);
    }
  }

  private subscribeToScenarioChanges(formArray: FormArray, index: number): void {
    const control = formArray.at(index).get('investmentScenario');

    control.valueChanges.subscribe(newValue => {
      formArray.controls.forEach((group) => {
        const scenarioControl = group.get('investmentScenario');
        const isSelected = group.get('investmentType').value;

        if (isSelected && scenarioControl.value !== newValue) {
          scenarioControl.setValue(newValue, { emitEvent: false });
        }
      });
    });
  }

  public constructPayload(formArray: FormArray, formGroup: FormGroup, isEmployee: boolean): any {
    const formGroupValue = formGroup.value;
    const allocationKey = isEmployee ? 'investmentAllocations' : 'pdkpInvestmentAllocations';
    const commonFields = {
        annualSalaryIncrease: Number((formGroupValue.avgSalaryIncYear).replace(GENERAL_CONSTANTS.PERCENTAGE, GENERAL_CONSTANTS.EMPTY_STRING)),
        [allocationKey]: {
            fundList: formArray.controls.map((control, index) => {
                const investment = control.value;
                if (investment.investmentType) {
                    return {
                        fundType: index + 1,
                        fundName: investment.investmentName,
                        percentage: Number(investment.investmentPercentage),
                        ...(!isEmployee && { scenario: Number(investment.investmentScenario) })
                    };
                }
            }).filter(Boolean)
        }
    };

    if (isEmployee) {
      return {
        ...commonFields,
        monthlyContribution: Number((formGroupValue.totalCurrentMonthlyContribution).replace(/[^\d\.]/g, GENERAL_CONSTANTS.EMPTY_STRING)),
        dateOfBirth: formGroupValue.dateOfBirth,
        gender: Number(formGroupValue.userGender),
        salary: Number((formGroupValue.monthlyIncome).replace(/[^\d\.]/g, GENERAL_CONSTANTS.EMPTY_STRING)),
        pensionAge: Number((formGroupValue.retirementAge).replace(/[^0-9]/g, GENERAL_CONSTANTS.EMPTY_STRING)),
        initialBalance: Number(formGroupValue.pensionSavingBalance),
        annualContributionIncrease: Number((formGroupValue.annualDevResults).replace(GENERAL_CONSTANTS.PERCENTAGE, GENERAL_CONSTANTS.EMPTY_STRING)),
      };
    }

    return {
      ...commonFields,
      effectiveDate: formGroupValue.effectiveDate.formatted ?? GENERAL_CONSTANTS.EMPTY_STRING,
    };
  }

  public constructUploadPayload(file: File): FormData {
    const fileDetails = this.extractFileDetails(file.name);
    const request: DocumentUploadRequest = {
      userId: this.accountDetails.id,
      file: file,
      type: fileDetails.extension,
      status: '1',
      documentType: 'upload-document',
      title: fileDetails.title,
      language: this.lang,
      companyCode: GENERAL_CONSTANTS.EMPTY_STRING
    };

    const formData: FormData = new FormData();
    formData.append('userId', request.userId);
    formData.append('file', request.file, request.file.name);
    formData.append('status', request.status);
    formData.append('language', request.language);
    formData.append('type', request.type);
    formData.append('documentType', request.documentType);

    return formData;
  }

  public constructDownloadPayload(request: DocumentDownloadRequest): FormData {
    const formData: FormData = new FormData();
    formData.append('userId', request.userId);
    formData.append('documentType', request.documentType);
    formData.append('docExtension', request.docExtension);
    formData.append('docCaption', request.docCaption);

    return formData;
  }

  public extractFileDetails(filename: string): { title: string, extension: string } {
    const lastDotIndex = filename.lastIndexOf(GENERAL_CONSTANTS.DOT);
    const title = lastDotIndex !== -1 ? filename.substring(0, lastDotIndex) : filename;
    const extension = lastDotIndex !== -1 ? filename.substring(lastDotIndex + 1) : '';

    return { title, extension };
  }

  public uploadValidation(file: File): void {
    this.clearError();

    const allowedExtensions = [FILE_TYPE_CONSTANTS.XLS, FILE_TYPE_CONSTANTS.XLSX, FILE_TYPE_CONSTANTS.CSV];
    const fileExtension = file.name.split(GENERAL_CONSTANTS.DOT).pop()?.toLowerCase();
    const maxSizeInBytes = 1 * 1024 * 1024;

    if (!fileExtension || allowedExtensions.indexOf(fileExtension) === -1) {
      this.showError("Upload Error: Wrong file format. Please upload a file with one of the following extensions: xls, xlsx, csv.");
      return;
    }

    if (file.size > maxSizeInBytes) {
      this.showError("Upload Error: File size exceeds the 1 MB limit. Please upload a smaller file.");
      return;
    }

    this.furtherUpload = true;
  }

  public percentageInputValidation(formArray: FormArray): number {
    formArray.controls.forEach((investmentGroup: FormGroup) => {
      const percentageControl = investmentGroup.get('investmentPercentage');
      if (!percentageControl.value) {
        percentageControl.setValue(0);
      }
    });

    const checkedInvestments = this.getCheckedInvestments(formArray);
    return checkedInvestments.reduce((total, investment) => total + Number(investment.investmentPercentage), 0);
  }

  private getCheckedInvestments(formArray: FormArray): any[] {
    return formArray.controls
      .filter((investmentGroup: FormGroup) => investmentGroup.get('investmentType').value)
      .map((investmentGroup: FormGroup) => ({
        investmentType: investmentGroup.get('investmentType').value,
        investmentName: investmentGroup.get('investmentName').value,
        investmentPercentage: investmentGroup.get('investmentPercentage').value
      }));
  }

  public onChange(event: any) {
    this.clearError();
    const fileList: FileList = event.target.files;
    if (fileList.length > 0) {
      const file: File = fileList[0];

      this.uploadValidation(file);
      const requestUpload: FormData = this.constructUploadPayload(file);

      if (this.furtherUpload) {
        this.documentResourceService.uploadDocumentSourceFile(requestUpload).subscribe(
          data => {
            this.noTitle = false;
            this.docStatus = data;

            // if (data.replyMessageList && data.replyMessageList.length > 0) {
            //   const response = data.replyMessageList[0];
            // }

            if (this.docStatus.status = 200) {
              // const replyMessageList = Array.isArray(this.docStatus.replyMessageList) ? this.docStatus.replyMessageList[0] : [];
              this.error = null;
              this.success = true;
              this.uploadedFile = true;
              this.attachFile = file;
              // this.getDocuments();
            } else {
              this.uploadedFile = false;
              this.success = false;
              this.showError('ERROR');
            }
          },
          error => {
            this.success = false;
            this.showError('INTERROR');
          }
        );
      }
    }
  }

  public downloadDocument(): void {
    const filename = this.documentDownloadRequest.docCaption + GENERAL_CONSTANTS.DOT + this.documentDownloadRequest.docExtension;
    const requestDownload: FormData = this.constructDownloadPayload(this.documentDownloadRequest);

    this.documentResourceService.downloadDocumentSourceFile(requestDownload).subscribe((docsData: Blob) => {
    this.noFile = false;
      try {
        FileSaver.saveAs(docsData, filename);
      } catch (e) {
        console.log('Error : ' + e);
      }
    }, (err) => {
      this.noFile = true;
    });
  }

  public setDefaultValue(): void {
    this.showForm = false;
    let val = 0;
    this.pensionSetupService.getDefaultSetupValues(val)
      .subscribe(
        (response) => {
          this.showForm = true;
          this.defaultsetupValues = response;
          this.avgSalaryIncYear.patchValue(
            this.defaultsetupValues["avgSalaryIncYear"] + GENERAL_CONSTANTS.PERCENTAGE
          );
          this.pensionSavingBalance.patchValue(this.balanceAmount);
          this.totalCurrentMonthlyContribution.patchValue(
            this.defaultsetupValues["pensionContribution"]
          );
        },
        (error) => {
          this.showForm = false;
          this.error = true;
        }
      );

    if (!this.isEmployee && this.accountDetails) {
      this.documentDownloadRequest = {
        userId: this.accountDetails.id || undefined,
        documentType: 'template',
        docCaption: 'Template Data Peserta',
        docExtension: 'xlsx'
      }
    }
  }

  public triggerScrollToOffsetOnly(offset: number = 0): void {}

  public formValueGetChanged(): void {
    this.pensionCalculatorForm.valueChanges.subscribe(
      data => {
        this.showPensionResult = false;
    });

    this.pensionCalculatorEmployerForm.valueChanges.subscribe(
      data => {
        this.showPensionResult = false;
    });
  }

  public handlePercentage(event: Event, formControl: FormControl): void {
    const formControlValue = formControl.value.replace(GENERAL_CONSTANTS.PERCENTAGE, GENERAL_CONSTANTS.EMPTY_STRING);

    if (Number(formControlValue) > 100) {
      formControl.patchValue(GENERAL_CONSTANTS.EMPTY_STRING);
    }
  }

  public showError(message: string): void {
    this.error = true;
    this.errorMessage = message;
  }

  public clearError(): void {
    this.errorMessage = GENERAL_CONSTANTS.EMPTY_STRING;
    this.error = false;
  }
}
